import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Check, Folder, Loader2, Trash2 } from 'lucide-react';
import { api } from '../../../../services/api'; 

interface GoogleDriveProps {
  userId: string;
  isConnected: boolean;
  onStatusChange: (status: boolean) => void;
}

export const GoogleDriveSettings: React.FC<GoogleDriveProps> = ({ userId, isConnected, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const hasRunOAuth = useRef(false);

  // --- 1. Handle OAuth Redirect ---
  useEffect(() => {
    const handleOAuth = async () => {
      // 1. Get Params
      const params = new URLSearchParams(window.location.search);
      const authCode = params.get('code');
      const state = params.get('state');

      // Debug Logs (Check your Console F12 if nothing happens)
      if (authCode) console.log("PowerPack: Auth Code detected:", authCode);
      if (state) console.log("PowerPack: State detected:", state);

      // Guard: Ensure we have a code and haven't run already
      if (hasRunOAuth.current || !authCode) return;

      // 2. Start Processing
      hasRunOAuth.current = true;
      setLoading(true);

      try {
        console.log("PowerPack: Exchanging code for tokens...");
        
        // Clean URL immediately to prevent double-submit
        window.history.replaceState({}, document.title, window.location.pathname);

        const result = await api.exchangeGoogleCode(userId, authCode);
        console.log("PowerPack: API Result:", result);

        // 3. Success Check (More Robust)
        // If the API didn't throw an error, we assume it worked.
        // We also check if 'result' exists to be safe.
        if (result) {
          onStatusChange(true); 
          alert("Google Drive Connected Successfully!");
        }
      } catch (error: any) {
        console.error("PowerPack: OAuth Error", error);
        alert(`Failed to connect: ${error.message || "Unknown Error"}`);
      } finally {
        setLoading(false);
      }
    };

    handleOAuth();
  }, [userId, onStatusChange]);

  // --- 2. Actions ---
  const handleConnect = async () => {
    setLoading(true);
    try {
      await api.initiateGoogleAuth(userId);
    } catch (error) {
      console.error("Auth URL Error", error);
      alert("Could not start Google connection");
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Are you sure? Videos will stop syncing to your Drive.")) return;
    setLoading(true);
    try {
      await api.disconnectGoogle(userId);
      onStatusChange(false);
    } catch (e) {
      alert("Failed to disconnect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
      <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-100">
        <Folder className="text-yellow-600" size={24} />
        <h3 className="text-lg font-bold text-slate-800">Video Storage</h3>
      </div>

      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" alt="Drive" className="w-8 h-8" />
          </div>
          <div>
            <h4 className="font-bold text-slate-900">Google Drive</h4>
            <p className="text-xs text-slate-500">
              {isConnected ? 'Connected & Syncing' : 'Not connected'}
            </p>
          </div>
        </div>

        {isConnected ? (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-lg font-medium text-sm">
              <Check size={16} /> Connected
            </div>
            <button 
                onClick={handleDisconnect} 
                disabled={loading}
                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Disconnect"
            >
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
            </button>
          </div>
        ) : (
          <button
            onClick={handleConnect}
            disabled={loading}
            className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
            Connect
          </button>
        )}
      </div>
    </div>
  );
};
